import { useState } from "react";
import styled from "styled-components";
import { fontStyles } from "../../../../../Component/style/admin_font";
import { RowView } from "../../../../../Component/style/common_style";
import { MatchRow, MatchStatus, MatchedImage, optionLabel } from "./types";

const statusColor: Record<MatchStatus, string> = {
  성공: "#1aa053",
  없음: "var(--admin-muted)",
  오류: "#eb003b",
};

const won = (n: number | null) => (n === null ? "-" : n.toLocaleString());

/** 오류 셀 — 1건: 바로 노출 / 2건+: "총 N개" + 접기·펼치기 */
const ErrorCell = ({ row }: { row: MatchRow }) => {
  const [open, setOpen] = useState(false);
  const { errors } = row;

  if (errors.length === 0) return <Dash>-</Dash>;

  if (errors.length === 1) {
    return <ErrText>{errors[0].message}</ErrText>;
  }

  return (
    <ErrColumn>
      <ErrSummary onClick={() => setOpen((v) => !v)}>
        <span>
          총 <b>{errors.length}개</b>의 오류가 있습니다.
        </span>
        <Caret className={open ? "open" : ""}>⌄</Caret>
      </ErrSummary>
      {open && (
        <ErrList>
          {errors.map((e, i) => (
            <li key={i}>{e.message}</li>
          ))}
        </ErrList>
      )}
    </ErrColumn>
  );
};

const MatchResultTable = ({ rows, previewImages = [], emptyMessage }: {
  rows: MatchRow[];
  previewImages?: MatchedImage[];
  emptyMessage?: string;
}) => {
  return (
    <TableWrap>
      <HeadRow>
        <div className="name">{previewImages.length && !rows.length ? "파일명" : "상품명"}</div>
        <div className="price">가격</div>
        <div className="stock">재고</div>
        <div className="policy">PG 여부</div><div className="policy">면세여부</div>
        <div className="delivery">개별 배송</div>
        <div className="option">옵션</div>
        <div className="img">이미지</div>
        <div className="status">상태</div>
        <div className="error">오류</div>
      </HeadRow>

      <Body>
        {rows.length === 0 && previewImages.length > 0 ? (
          previewImages.map((image) => (
            <BodyRow key={image.name}>
              <div className="name"><Ellipsis title={image.name}>{image.name}</Ellipsis></div>
              <div className="price">-</div>
              <div className="stock">-</div>
              <div className="policy">-</div><div className="policy">-</div><div className="delivery">-</div>
              <div className="option">-</div>
              <div className="img"><Thumb src={image.url} alt={image.name} /></div>
              <div className="status"><WaitingText>엑셀 대기</WaitingText></div>
              <div className="error"><Dash>-</Dash></div>
            </BodyRow>
          ))
        ) : rows.length === 0 ? (
          <Empty>{emptyMessage || "엑셀이나 이미지를 업로드하면 결과가 표시됩니다."}</Empty>
        ) : (
          rows.map((m) => {
            const thumb = m.matched[0];
            return (
              <BodyRow key={m.row.rowIndex}>
                <div className="name">
                  <Ellipsis title={m.row.name}>{m.row.name}</Ellipsis>
                </div>
                <div className="price">{won(m.row.price)}</div>
                <div className="stock">{won(m.row.stock)}</div>
                <div className="policy">{m.row.isPg === false ? "X" : "Y"}</div><div className="policy">{m.row.isTaxExempt ? "Y" : "X"}</div>
                <div className="delivery">
                  <input type="checkbox" checked={m.row.individualDelivery} readOnly
                    aria-label={`${m.row.name} 개별 배송`} tabIndex={-1} />
                  <span>{m.row.individualDelivery ? `${won(m.row.individualDeliveryPrice ?? 0)}원` : "-"}</span>
                </div>
                <div className="option">
                  <Ellipsis title={optionLabel(m.row)}>
                    {optionLabel(m.row) || "-"}
                  </Ellipsis>
                </div>
                <div className="img">
                  {thumb ? (
                    <Thumb src={thumb.url} alt={thumb.name} />
                  ) : (
                    <ThumbEmpty />
                  )}
                </div>
                <div className="status">
                  <StatusText style={{ color: statusColor[m.status] }}>
                    {m.status}
                  </StatusText>
                </div>
                <div className="error">
                  <ErrorCell row={m} />
                </div>
              </BodyRow>
            );
          })
        )}
      </Body>
    </TableWrap>
  );
};

export default MatchResultTable;

const TableWrap = styled.div`
  > * { flex-shrink: 0; min-width: 900px; }
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--admin-theme-border, #ececec);
  border-radius: 8px;
  overflow-x: auto;
  min-height: 0;
`;

const rowLayout = `
  .policy { flex: 0 0 5rem; justify-content: center; }
  .delivery {
    flex: 0 0 calc(var(--control-md) * 3);
    justify-content: center; gap: var(--space-1);
    input { accent-color: var(--admin-accent); pointer-events: none; }
  }
  .name { flex: 1.4; }
  .price { flex: 0.8; }
  .stock { flex: 0.7; }
  .option { flex: 1.4; }
  .img { flex: 0.7; display: flex; align-items: center; justify-content: center; }
  .status { flex: 0.7; }
  .error { flex: 2.2; }
`;

const HeadRow = styled(RowView)`
  ${rowLayout}
  gap: 0.5rem;
  padding: 0.9rem 1.2rem;
  border-bottom: 1px solid var(--admin-theme-border, #ececec);
  color: var(--admin-theme-text, #555);
  font-size: 15px;
  ${fontStyles.semiBold}
  > div {
    box-sizing: border-box;
    padding: 0 0.8rem;
    border: 1px solid transparent;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--admin-theme-raised, #e2e2e2);
    border-radius: 6px;
  }
`;

const BodyRow = styled(RowView)`
  ${rowLayout}
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.35rem 1.2rem;
  background: var(--admin-theme-surface, #ffffff);
  font-size: 15px;
  color: var(--admin-ink);
  ${fontStyles.regular}
  > div {
    box-sizing: border-box;
    min-width: 0;
    min-height: 3rem;
    display: flex;
    align-items: center;
    background: var(--admin-theme-surface, #fbfbfb);
    border: 1px solid var(--admin-border);
    border-radius: 6px;
    padding: 0.4rem 0.8rem;
  }
  .img,
  .status,
  .error {
    background: transparent;
  }
  .status,
  .error {
    border-color: transparent;
  }
  .img {
    padding: 0;
    border: none;
    justify-content: center;
  }
`;

const Ellipsis = styled.span`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Thumb = styled.img`
  box-sizing: border-box;
  width: 3rem;
  height: 3rem;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid var(--admin-border);
`;

const ThumbEmpty = styled.div`
  box-sizing: border-box;
  width: 3rem;
  height: 3rem;
  border-radius: 6px;
  border: 1px solid var(--admin-border);
  background: var(--admin-theme-surface, #fbfbfb);
`;

const WaitingText = styled.span`
  ${fontStyles.semiBold}
  font-size: 15px;
  color: var(--admin-muted);
`;

const StatusText = styled.span`
  ${fontStyles.semiBold}
  font-size: 15px;
`;

const Dash = styled.div`
  width: 100%;
  text-align: center;
  color: var(--admin-theme-muted, #b8b8b8);
`;

const ErrText = styled.div`
  color: var(--admin-theme-text, #555);
  line-height: 1.5;
`;

const ErrColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ErrSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: var(--admin-theme-text, #555);
  b {
    color: var(--admin-theme-danger, #eb003b);
    ${fontStyles.semiBold}
  }
`;

const Caret = styled.span`
  margin-left: 0.5rem;
  font-size: 22px;
  line-height: 1;
  transition: transform 0.2s;
  &.open {
    transform: rotate(180deg);
  }
`;

const ErrList = styled.ul`
  margin: 0.5rem 0 0;
  padding-left: 1.1rem;
  li {
    list-style: disc;
    color: var(--admin-theme-text, #555);
    line-height: 1.7;
    font-size: 14px;
  }
`;

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 20rem;
  color: var(--admin-theme-muted, #9e9e9e);
  font-size: 15px;
`;
